import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { uniqBy } from 'lodash';
import { Button } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../../store';
import { selectAccountState } from '../../account/api/accountSlice';
import { uploadMessageAttachmentsThunk } from '../api/composeSlice';
import {
  getMessageByIdThunk,
  getMessagesThunk,
  selectInboxState,
  updateMailboxMessageThunk,
  removeOldThreadMessage,
  selectMessageByThreadId,
} from '../api/inboxSlice';
import { usePostMessageMutation } from '../../../api/services/MessageService';
import { MessageReplySchema } from '../schemas';
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPromt';
import usePrevious from '../../../hooks/usePrevious';

import MessageAttachments from './MessageAttachments';
import MessageThreadHeader from './MessageThreadHeader';
import MessageThreadItem from './MessageThreadItem';
import CancelActionModal from '../../app/components/CancelModal';
import CustomModal from '../../../components/modal/CustomModal';
import Divider from '../../../components/common/Divider';
import LoaderButton from '../../../components/common/LoaderButton';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import Textarea from '../../../features/app/components/TextArea';

import { FIELD_TEXT_MESSAGES } from '../../../config';
import alert from '../../../utils/alert';
import { downloadAttachment, usernameFromFHIR4HumanName } from '../../../utils/general';

import mailGlowIcon from '../../../assets/images/icons/mail-glow-icon.svg';
import SentIcon from '../../../assets/images/icons/sent-icon.png';

import '../Messages.scss';
import useIsMobile from '../../../hooks/useIsMobile';

const MAX_FILE_SIZE = 20000000;
const FILE_SIZE_UNIT = 1000000; // 1 MB
const MAX_MESSAGE_LENGTH = 7000;

const defaultValues = { body: '' };

const MessageThreadList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { patient, sidebarMenuCollapsed } = useAppSelector(selectAccountState);
  const { activeMessage } = useAppSelector(selectInboxState);
  const { threadId } = useParams();

  const [postMessage] = usePostMessageMutation();
  const [isThreadLoading, setIsThreadLoading] = useState(true);
  const [showConfirmMessageDialog, setShowConfirmMessageDialog] = useState(false);
  const [, setIsMessageReplied] = useState(false);
  const [clearAttachments, setClearAttachments] = useState(false);

  const [isMessageEmpty, setIsMessageEmpty] = useState(true);
  const [attachments, setAttachments] = useState<Array<{ file: File | Blob; name: string }> | null>(
    null,
  );

  const selectedMessageByThreadId = useAppSelector(selectMessageByThreadId(threadId));
  const mailboxId = selectedMessageByThreadId?.id;

  const lastMessageRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(MessageReplySchema),
  });

  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesPrompt(
    isDirty && !isSubmitting,
  );

  const participants = useMemo(
    () =>
      activeMessage
        ? uniqBy(
            [activeMessage.from, ...activeMessage.to.map((item: any) => item.recipient)],
            'reference',
          )
        : [],
    [activeMessage],
  );

  const recipients = useMemo(
    () => participants?.filter((participant: any) => patient?.id !== participant.reference),
    [participants, patient?.id],
  );

  const recipientAvatars = useMemo(
    () =>
      recipients?.reduce(
        (acc: any, item: any) =>
          item.avatar ? [...acc, ...item.avatar] : [...acc, { name: item.display }],
        [],
      ) || [],
    [recipients],
  );
  const recipientNames = useMemo(
    () => recipients?.map((recipient: any) => recipient.display),
    [recipients],
  );

  const sortedThread = useMemo(
    () =>
      activeMessage
        ? [
            activeMessage.message,
            ...(activeMessage.thread?.map((i: any) => i.message) || []),
          ].reverse()
        : [],
    [activeMessage],
  );

  const onSubmit = useCallback(
    async (formData: typeof defaultValues) => {
      try {
        if (!patient?.identifier || !patient.name || !activeMessage) return;

        const attachmentsSize = attachments?.reduce((acc, item) => acc + item.file.size, 0) || 0;
        if (attachmentsSize > MAX_FILE_SIZE) {
          throw new Error(
            t(
              `The total attachments size exceeds the limit of ${Math.floor(MAX_FILE_SIZE / FILE_SIZE_UNIT)} MB`,
            ),
          );
        }

        if (formData.body.length > MAX_MESSAGE_LENGTH) {
          alert.error(
            t(`You have exceeded the maximum of ${MAX_MESSAGE_LENGTH} characters limit`),
            { toastId: 'message-length-error' },
          );
          return;
        }

        setIsMessageReplied(true);
        const messageContent: any = {
          ...formData,
          subject: activeMessage.message?.header.subject,
          from: {
            reference: patient.id,
            identifier: patient.identifier.find((i: any) => i.system?.includes('username')),
            type: patient.resourceType,
            display: usernameFromFHIR4HumanName(patient.name[0]),
          },
          to: recipients,
          threadId: activeMessage.threadId || activeMessage.messageId,
        };

        if (attachments?.length) {
          const uploadedAttachments = await dispatch(
            uploadMessageAttachmentsThunk({ attachments }),
          ).unwrap();
          if (uploadedAttachments) {
            messageContent.contentType = 'attachment';
            messageContent.attachment = uploadedAttachments
              .filter((attachment: any) => attachment?.resource?.type === 'File')
              .map((attachment: any) => ({ ...attachment.resource }));
          }
        }

        const postMsgRes = await postMessage(messageContent).unwrap();
        const threadId = postMsgRes?.resource?.[0]?.threadId || postMsgRes?.resource?.[0]?.id;

        if (threadId) {
          await dispatch(getMessagesThunk({ personId: patient.id, limit: 10, offset: 0 })).unwrap();
          await dispatch(removeOldThreadMessage(activeMessage.id));
          navigate(`${threadId}`);
        }

        alert.success(t(FIELD_TEXT_MESSAGES.MESSAGE_SENT_SUCCESSFULLY), {
          toastId: 'message-compose-success',
        });
      } catch (error: any) {
        alert.error(t('Something went wrong. Please try again.'), {
          toastId: 'message-reply-error',
        });
      } finally {
        if (formData.body.length <= MAX_MESSAGE_LENGTH) {
          setClearAttachments(true);
          reset();
        }
      }
    },
    [patient, activeMessage, recipients, attachments, postMessage, dispatch, navigate, reset, t],
  );

  useEffect(() => {
    if (clearAttachments) {
      setAttachments(null);
      setClearAttachments(false);
    }
  }, [clearAttachments]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (!mailboxId || activeMessage?.read || activeMessage?.from.reference === patient?.id)
        return;

      const messages = await dispatch(
        getMessagesThunk({ personId: patient?.id || '', limit: 10, offset: 0 }),
      ).unwrap();
      const activeMailboxId = messages?.resource?.entry?.find(
        (item: any) => item.resource.threadId === threadId,
      )?.resource.id;
      await dispatch(updateMailboxMessageThunk(activeMailboxId || mailboxId));
    }, 1000);

    return () => clearTimeout(timeout);
  }, [
    activeMessage?.from.reference,
    activeMessage?.read,
    dispatch,
    mailboxId,
    patient?.id,
    threadId,
  ]);

  const previousThreadId = usePrevious(threadId);
  useEffect(() => {
    if (previousThreadId !== threadId) {
      setIsThreadLoading(true);
      reset();
    }
    mailboxId &&
      dispatch(getMessageByIdThunk(mailboxId))
        .unwrap()
        .finally(() => setIsThreadLoading(false));
  }, [dispatch, mailboxId, previousThreadId, reset, threadId]);

  const previousMessagesLength = usePrevious(sortedThread?.length);
  useEffect(() => {
    if (!isThreadLoading || previousMessagesLength !== sortedThread?.length) {
      lastMessageRef.current?.scrollIntoView({ block: 'start' });
      setIsMessageReplied(false);
    }
  }, [isThreadLoading, previousMessagesLength, sortedThread?.length]);

  const onAttachmentsUploaded = useCallback((files: Array<{ file: File | Blob; name: string }>) => {
    setAttachments(files);
  }, []);

  if (isThreadLoading || !activeMessage) return <LoadingIndicator />;

  return (
    <div className='messages__thread-wrapper'>
      <MessageThreadHeader
        time={activeMessage.message.timestamp}
        avatars={recipientAvatars}
        from={recipientNames}
      />
      <Divider />
      <div className='message__thread-body'>
        <div className='message__thread-body-container'>
          {sortedThread.map((message: any, index: number) => (
            <React.Fragment key={message?.id}>
              {index !== 0 && <Divider />}
              <MessageThreadItem
                subject={message.header.subject}
                content={message.body.content}
                sender={message.header.from.display || ''}
                senderType={
                  message.header.from.type === 'PractitionerRole' ? 'Provider' : 'Patient'
                }
                timeSent={message.timestamp}
                avatarSrc={
                  participants?.find(
                    (participant: any) => participant.reference === message.header.from.reference,
                  )?.avatar?.[0]?.url
                }
                containerRef={index + 1 === sortedThread.length ? lastMessageRef : undefined}
                attachments={message.body.attachment}
                downloadAttachment={downloadAttachment}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div
        className='bg-gray-100  p-md-4'
        style={isMobile ? {} : { left: sidebarMenuCollapsed ? '462px' : '660px' }}
      >
        <form>
          <fieldset disabled={isSubmitting} className='message__thread-list-fieldset'>
            <Controller
              name='body'
              control={control}
              render={({ field: { ref, onChange, ...nonRefField } }) => (
                <div className='message__thread-textarea'>
                  <Textarea
                    id='reply'
                    placeholder={t('Type message here')}
                    className={errors.body ? 'is-invalid' : ''}
                    rows={isMobile ? 1 : 4}
                    style={{ resize: 'none', whiteSpace: 'pre-wrap' }}
                    ref={ref}
                    onChange={(e) => {
                      onChange(e);
                      setIsMessageEmpty(e.target.value.trim() === '');
                    }}
                    {...nonRefField}
                  />
                  {isMobile && (
                    <button
                      style={{ background: 'none', border: 'none' }}
                      type='submit'
                      disabled={isSubmitting || isMessageEmpty}
                      onClick={() => setShowConfirmMessageDialog(true)}
                    >
                      <img
                        src={SentIcon}
                        alt='send indicator'
                        style={{ width: '24px', height: '24px', marginLeft: '5px' }}
                      />
                    </button>
                  )}
                  {errors.body && <div className='invalid-feedback'>{errors.body.message}</div>}
                </div>
              )}
            />
            <div className='mt-0 message__thread-attachment'>
              <MessageAttachments
                clearUploadedFiles={clearAttachments}
                onFileUpload={onAttachmentsUploaded}
                showAttachmentIcon={true}
                isSubmitting={isSubmitting}
              />
            </div>
            {!isMobile && (
              <div
                style={{ columnGap: '12px' }}
                className='mt-0  d-flex justify-content-end flex-column gap-3'
              >
                <LoaderButton
                  style={{ width: '162px' }}
                  className='bg-primary-500 text-white'
                  type='button'
                  disabled={isSubmitting || isMessageEmpty}
                  onClick={() => setShowConfirmMessageDialog(true)}
                >
                  {t('Send')}
                </LoaderButton>
                <Button
                  style={{ width: '162px' }}
                  className='text-gray-700 bg-white border-gray-300'
                  onClick={() => navigate('../')}
                  disabled={isSubmitting}
                >
                  {t('Cancel')}
                </Button>
              </div>
            )}
          </fieldset>
          <CancelActionModal
            show={showPrompt}
            title='Cancel message'
            helpText='Are you sure you want to cancel and delete this message?'
            confirmText='Yes, Delete'
            cancelText='No'
            onCancel={cancelNavigation}
            onConfirm={confirmNavigation}
          />
          <CustomModal
            size='sm'
            show={showConfirmMessageDialog}
            showCustomHeaderIcon
            customHeaderIcon={<img src={mailGlowIcon} alt='mail icon' />}
            title={t('Send message')}
            helpText={t('Are you sure you want to send this message?')}
            confirmText='Send'
            cancelText='Cancel'
            handleConfirm={() => {
              setShowConfirmMessageDialog(false);
              handleSubmit(onSubmit)();
            }}
            handleCancel={() => setShowConfirmMessageDialog(false)}
          />
        </form>
      </div>
    </div>
  );
};

export default MessageThreadList;
